import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serviceTemplateScheduleContract } from "@common/contracts/service-template-schedule.contract";
import {
    OccurrenceBulkActionResponse,
    OccurrenceBulkActionType,
    OccurrenceBulkRoleAssignmentField,
    OccurrenceRoleOverride,
    ResolvedOccurrenceResponse,
} from "@common/schemas/serviceTemplateRecurrenceOccurrence.schema";
import { ServiceTemplateScheduleOccurrencesSortValue } from "@common/types/views/serviceTemplateScheduleOccurrencesView";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { extractApiErrorMessage } from "./apiError";
import { serviceTemplateScheduleQueryKeys } from "./serviceTemplateSchedule.api";

const serviceTemplateScheduleClient = getBackendAPIClient(
    serviceTemplateScheduleContract,
);

const occurrenceQueryKeys = {
    list: (
        organizationId: string,
        scheduleId: string,
        sort?: ServiceTemplateScheduleOccurrencesSortValue,
    ) =>
        [
            ...serviceTemplateScheduleQueryKeys.organization(organizationId),
            "occurrences",
            scheduleId,
            sort,
        ] as const,
};

interface UseServiceTemplateScheduleOccurrencesOptions {
    organizationId: string | null | undefined;
    scheduleId: string | null | undefined;
    sort?: ServiceTemplateScheduleOccurrencesSortValue;
    enabled?: boolean;
}

export function useServiceTemplateScheduleOccurrences(
    options: UseServiceTemplateScheduleOccurrencesOptions,
) {
    const { organizationId, scheduleId, sort, enabled = true } = options;

    return useQuery({
        queryKey:
            organizationId && scheduleId
                ? occurrenceQueryKeys.list(organizationId, scheduleId, sort)
                : serviceTemplateScheduleQueryKeys.all(),
        queryFn: async (): Promise<ResolvedOccurrenceResponse[]> => {
            if (!organizationId || !scheduleId) {
                return [];
            }

            const response =
                await serviceTemplateScheduleClient.listOccurrences({
                    params: { organizationId, scheduleId },
                    query: { sort: sort ?? undefined },
                });

            if (response.status !== 200) {
                throw new Error("Failed to fetch schedule occurrences");
            }

            return response.body.occurrences;
        },
        enabled: !!organizationId && !!scheduleId && enabled,
    });
}

interface OccurrenceActionMutationVariables {
    organizationId: string;
    occurrenceId: string;
}

export function useApplyOccurrenceNowMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceId,
        }: OccurrenceActionMutationVariables) => {
            const response =
                await serviceTemplateScheduleClient.applyOccurrenceNow({
                    params: { organizationId, occurrenceId },
                });

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to apply occurrence",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useSkipOccurrenceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceId,
        }: OccurrenceActionMutationVariables) => {
            const response = await serviceTemplateScheduleClient.skipOccurrence(
                { params: { organizationId, occurrenceId } },
            );

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to skip occurrence",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useUnskipOccurrenceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceId,
        }: OccurrenceActionMutationVariables) => {
            const response =
                await serviceTemplateScheduleClient.unskipOccurrence({
                    params: { organizationId, occurrenceId },
                });

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to undo skip",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

interface BulkOccurrenceActionMutationVariables {
    organizationId: string;
    occurrenceIds: string[];
    action: OccurrenceBulkActionType;
}

export function useBulkOccurrenceActionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceIds,
            action,
        }: BulkOccurrenceActionMutationVariables): Promise<OccurrenceBulkActionResponse> => {
            const response =
                await serviceTemplateScheduleClient.bulkOccurrenceAction({
                    params: { organizationId },
                    body: { occurrenceIds, action },
                });

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to perform bulk action on occurrences",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

interface BulkUpdateOccurrenceRoleAssignmentMutationVariables {
    organizationId: string;
    occurrenceIds: string[];
    field: OccurrenceBulkRoleAssignmentField;
    value: number | null;
}

export function useBulkUpdateOccurrenceRoleAssignmentMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceIds,
            field,
            value,
        }: BulkUpdateOccurrenceRoleAssignmentMutationVariables): Promise<OccurrenceBulkActionResponse> => {
            const response =
                await serviceTemplateScheduleClient.bulkUpdateOccurrenceRoleAssignment(
                    {
                        params: { organizationId },
                        body: { occurrenceIds, field, value },
                    },
                );

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to update role assignment on occurrences",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

interface UpdateOccurrenceRoleOverrideMutationVariables extends OccurrenceActionMutationVariables {
    override: OccurrenceRoleOverride;
}

export function useUpdateOccurrenceRoleOverrideMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            occurrenceId,
            override,
        }: UpdateOccurrenceRoleOverrideMutationVariables) => {
            const response =
                await serviceTemplateScheduleClient.updateOccurrenceRoleOverride(
                    {
                        params: { organizationId, occurrenceId },
                        body: override,
                    },
                );

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to update role assignment",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}
